import type {
  TeamDashboardBuildCountKey,
  TeamDashboardColumnDefinition,
  TeamDashboardColumnId,
  TeamDashboardMetricDefinition,
} from './types';

/**
 * 团队看板默认列
 * 字段来源：团队看板 Figma 与需求文档表格默认展示
 */
export const DEFAULT_TEAM_DASHBOARD_COLUMN_IDS: Record<
  'allDepartments' | 'singleTeam',
  TeamDashboardColumnId[]
> = {
  allDepartments: [
    'teamName',
    'ownerName',
    'memberCount',
    'spend',
    'impressions',
    'cpm',
    'clicks',
    'cpc',
    'conversion',
    'costPerConversion',
  ],
  singleTeam: [
    'memberName',
    'accountCount',
    'campaignCount',
    'adgroupCount',
    'adCount',
    'spend',
    'impressions',
    'cpm',
    'clicks',
    'cpc',
    'conversion',
    'costPerConversion',
  ],
};

/**
 * 团队看板列配置本地存储键
 * 两种视角分别保存，避免部门表与成员表的列配置互相覆盖
 */
export const TEAM_DASHBOARD_COLUMN_STORAGE_KEYS: Record<'allDepartments' | 'singleTeam', string> = {
  allDepartments: 'team-dashboard-all-departments-column-ids',
  singleTeam: 'team-dashboard-single-team-column-ids',
};

/**
 * 团队看板暂不开放的表格指标
 * 保留字段定义和数据适配，仅从列配置及历史本地列配置中隐藏，便于后续恢复
 */
export const TEAM_DASHBOARD_HIDDEN_COLUMN_IDS: TeamDashboardColumnId[] = [
  'purchase',
  'costPerPurchase',
  'purchaseRate',
];

/**
 * 团队看板图表可选指标
 * 字段来源：团队看板需求文档 3.2 指标下拉
 */
export const TEAM_DASHBOARD_METRIC_DEFINITIONS: TeamDashboardMetricDefinition[] = [
  { key: 'spend', label: '消耗', color: '#2F6BFF', valueType: 'cost' },
  { key: 'conversion', label: '转化', color: '#16A34A', valueType: 'absolute' },
  { key: 'conversionRate', label: '转化率', color: '#8B5CF6', valueType: 'rate' },
  { key: 'costPerConversion', label: '转化成本', color: '#F97316', valueType: 'cost' },
  { key: 'impressions', label: '展示数', color: '#22C7D8', valueType: 'absolute' },
  { key: 'cpm', label: '千展成本', color: '#FACC15', valueType: 'cost' },
  { key: 'clicks', label: '点击数', color: '#10B981', valueType: 'absolute' },
  { key: 'ctr', label: '点击率', color: '#14B8A6', valueType: 'rate' },
  { key: 'cpc', label: '点击成本', color: '#6366F1', valueType: 'cost' },
];

/**
 * 团队看板广告基建数量字段
 * 用于单部门成员明细和个人趋势，不参与部门投放指标图表
 */
export const TEAM_DASHBOARD_BUILD_COUNT_COLUMN_IDS: TeamDashboardBuildCountKey[] = [
  'campaignCount',
  'adgroupCount',
  'adCount',
];

/**
 * 团队看板列字段池
 * 字段来源：团队看板需求文档表格默认字段与自定义列字段
 */
export const TEAM_DASHBOARD_COLUMN_DEFINITIONS: TeamDashboardColumnDefinition[] = [
  { id: 'teamName', label: '所属团队', category: '基础信息' },
  { id: 'ownerName', label: '负责人', category: '基础信息' },
  { id: 'memberCount', label: '团队人数', category: '基础信息' },
  { id: 'memberName', label: '员工姓名', category: '基础信息' },
  { id: 'accountCount', label: '广告账户数', category: '基础信息' },
  {
    id: 'campaignCount',
    label: '推广系列数',
    configLabel: '推广系列',
    category: '广告基建数',
  },
  {
    id: 'adgroupCount',
    label: '广告组数',
    configLabel: '广告组',
    category: '广告基建数',
  },
  { id: 'adCount', label: '广告数', configLabel: '广告', category: '广告基建数' },
  { id: 'detail', label: '操作', category: '基础信息' },
  { id: 'spend', label: '消耗', category: '投放指标', isMetric: true, valueType: 'cost' },
  {
    id: 'impressions',
    label: '展示数',
    category: '投放指标',
    isMetric: true,
    valueType: 'absolute',
  },
  { id: 'cpm', label: '千展成本', category: '投放指标', isMetric: true, valueType: 'cost' },
  { id: 'clicks', label: '点击量', category: '投放指标', isMetric: true, valueType: 'absolute' },
  { id: 'cpc', label: '点击成本', category: '投放指标', isMetric: true, valueType: 'cost' },
  { id: 'ctr', label: '点击率', category: '投放指标', isMetric: true, valueType: 'rate' },
  {
    id: 'conversion',
    label: '转化量',
    category: '转化指标',
    isMetric: true,
    valueType: 'absolute',
  },
  {
    id: 'costPerConversion',
    label: '转化成本',
    category: '转化指标',
    isMetric: true,
    valueType: 'cost',
  },
  {
    id: 'conversionRate',
    label: '转化率',
    category: '转化指标',
    isMetric: true,
    valueType: 'rate',
  },
  {
    id: 'downloadStartRate',
    label: '下载率',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'rate',
  },
  {
    id: 'downloadStart',
    label: '下载数',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'absolute',
  },
  {
    id: 'costPerDownloadStart',
    label: '下载成本',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'cost',
  },
  {
    id: 'appInstall',
    label: '安装数',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'absolute',
  },
  {
    id: 'costPerAppInstall',
    label: '安装成本',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'cost',
  },
  {
    id: 'registration',
    label: '注册数',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'absolute',
  },
  {
    id: 'costPerRegistration',
    label: '注册成本',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'cost',
  },
  {
    id: 'registrationRate',
    label: '注册率',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'rate',
  },
  {
    id: 'purchase',
    label: '付费数',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'absolute',
  },
  {
    id: 'costPerPurchase',
    label: '付费成本',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'cost',
  },
  {
    id: 'purchaseRate',
    label: '付费率',
    category: '应用推广指标',
    isMetric: true,
    valueType: 'rate',
  },
];

/**
 * 团队看板字段映射
 */
export const TEAM_DASHBOARD_COLUMN_MAP = TEAM_DASHBOARD_COLUMN_DEFINITIONS.reduce(
  (acc, field) => {
    acc[field.id] = field;
    return acc;
  },
  {} as Record<TeamDashboardColumnId, TeamDashboardColumnDefinition>
);
